// app/javascript/controllers/zoom_controller.js
import { Controller } from "@hotwired/stimulus";

/**
 * Zoom controller:
 * - Mouse wheel zoom in/out (centered on cursor)
 * - Long-press (mousedown) + drag = pan
 * - Provides dataset {scale, posX, posY} on zoomable target
 */
export default class extends Controller {
  static targets = ["zoomable"];
  static values = {
    scale: { type: Number, default: 1 },
    posX: { type: Number, default: 0 },
    posY: { type: Number, default: 0 },
  };

  connect() {
    this.isDragging = false;
    this.dragStart = { x: 0, y: 0 };

    this.element.addEventListener("zoom:disable", () => this.disable())
    this.element.addEventListener("zoom:enable", () => this.enable())
    
    this.element.addEventListener("mousedown", this.startDrag.bind(this));
    window.addEventListener("mousemove", this.drag.bind(this));
    window.addEventListener("mouseup", this.stopDrag.bind(this));

    // init position
    this.applyTransform();
    this.isDragging = false;
    this.element.addEventListener("zoom:startDrag", (e) =>
      this.startDrag(e.detail.event),
    );
    this.element.addEventListener("zoom:drag", (e) =>
      this.drag(e.detail.event),
    );
    this.element.addEventListener("zoom:stopDrag", () => this.stopDrag());

    // bind wheel event
    this.zoomableTarget.addEventListener("wheel", this.onWheel.bind(this), {
      passive: false,
    });
  }
  enable() { this.enabled = true }
  disable() { this.enabled = false; this.isDragging = false }

  // ========================= 縮放 =========================
  onWheel(e) {
    e.preventDefault();
    const zoomIntensity = 0.1;
    const { offsetX, offsetY, deltaY } = e;

    const scaleFactor = deltaY < 0 ? 1 + zoomIntensity : 1 - zoomIntensity;
    const newScale = this.scaleValue * scaleFactor;

    // limit scale range
    if (newScale < 0.2 || newScale > 5) return;

    // calculate the new position to keep the point under the cursor stationary
    this.posXValue =
      offsetX - (offsetX - this.posXValue) * (newScale / this.scaleValue);
    this.posYValue =
      offsetY - (offsetY - this.posYValue) * (newScale / this.scaleValue);
    this.scaleValue = newScale;

    this.applyTransform();
  }

  // ========================= drag =========================

  startDrag(e) {
    if (e.button !== 0) return;
    if (e.target.closest(".device__icon")) {
      return;
    }
    
    // Ignore drag if clicking inside control panel area
    if (e.target.closest(".management") || 
        e.target.closest("#floorplan_body") ||
        e.target.closest(".management__contain") ||
        e.target.closest(".management__toolbar") ||
        e.target.closest("#management_section > .management")) {
      return;
    }
    
    // Ignore drag if interacting with input elements (sliders, inputs, etc.)
    if (e.target.closest("input[type='range']") || 
        e.target.closest("#power_percentage_slider") ||
        e.target.closest(".power-slider-container") ||
        e.target.tagName === "INPUT" ||
        e.target.tagName === "BUTTON" ||
        e.target.tagName === "SELECT") {
      return;
    }

    this.isDragging = true;
    this.dragStart = {
      x: e.clientX - this.posXValue,
      y: e.clientY - this.posYValue,
    };

    this.updateCursor("grabbing");
  }

  drag(e) {
    if (!this.isDragging) return;
    if (e.target.closest(".device__icon")) return;
    
    // Stop dragging if mouse moves into control panel area
    if (e.target.closest(".management") || 
        e.target.closest("#floorplan_body") ||
        e.target.closest(".management__contain") ||
        e.target.closest(".management__toolbar") ||
        e.target.closest("#management_section > .management")) {
      this.stopDrag(e);
      return;
    }
    
    // Stop dragging if interacting with input elements
    if (e.target.closest("input[type='range']") || 
        e.target.closest("#power_percentage_slider") ||
        e.target.closest(".power-slider-container") ||
        e.target.tagName === "INPUT" ||
        e.target.tagName === "BUTTON" ||
        e.target.tagName === "SELECT") {
      this.stopDrag(e);
      return;
    }
    
    this.posXValue = e.clientX - this.dragStart.x;
    this.posYValue = e.clientY - this.dragStart.y;
    this.applyTransform();
  }

  stopDrag(e) {
    this.isDragging = false;
    if (e.target.closest(".device__icon")) return;
    this.updateCursor("default");
  }

  updateCursor(style) {
    if (this.zoomableTarget) {
      this.zoomableTarget.style.cursor = style;
    }
  }


  // ========================= tool =========================
  applyTransform() {
    const t = `translate(${this.posXValue}px, ${this.posYValue}px) scale(${this.scaleValue})`;
    this.zoomableTarget.style.transform = t;

    // sync to dataset
    this.zoomableTarget.dataset.scale = this.scaleValue;
    this.zoomableTarget.dataset.posX = this.posXValue;
    this.zoomableTarget.dataset.posY = this.posYValue;
  }

  // zoom in/out buttons
  zoomIn() {
    this.scaleValue *= 1.2;
    this.applyTransform();
  }

  zoomOut() {
    this.scaleValue /= 1.2;
    this.applyTransform();
  }

  reset() {
    this.scaleValue = 1;
    this.posXValue = 0;
    this.posYValue = 0;
    this.applyTransform();
  }
}
